import {
  ChatFooter,
  ChatInput,
  CustomCTAVisualPreview,
  DefaultCTAVisualPreview,
  LiveChatUI,
  isDefaultCTAVisual,
} from "@jugl-web/domain-resources/chat-bot";
import {
  useCTAVisualSource,
  useChatImageSource,
} from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { cx, useTranslations } from "@jugl-web/utils";
import { FC, useMemo, useState } from "react";
import { BrowserWindowShell } from "../BrowserWindowShell";
import { ReactComponent as MessageBubbleIcon } from "./assets/message-bubble.svg";
import { ReactComponent as UrlIcon } from "./assets/url.svg";
import { LiveChatAppSampleConversation } from "./components/LiveChatAppSampleConversation";

export interface LiveChatAppPreviewProps {
  browserTabTitle: string;
  faviconUrl: string | File | null;
  businessName: string;
  businessLogoUrl: string | File | null;
  agentName: string;
  agentImage: string | File | null;
  ctaTitle: string;
  ctaSubtitle: string;
  ctaVisual: string | File | null;
  suggestions: string[];
  customButton: { title: string; url: string } | null;
  senderMessageBackgroundColor: string;
  screenMode: "welcome" | "sampleConversation";
}

export const LiveChatAppPreview: FC<LiveChatAppPreviewProps> = ({
  browserTabTitle,
  faviconUrl,
  businessName,
  businessLogoUrl,
  agentName,
  agentImage,
  ctaTitle,
  ctaSubtitle,
  ctaVisual,
  suggestions,
  customButton,
  senderMessageBackgroundColor,
  screenMode,
}) => {
  const { t } = useTranslations();

  const businessLogoSrc = useChatImageSource(businessLogoUrl ?? "");
  const faviconSrc = useChatImageSource(faviconUrl ?? "");
  const ctaVisualSrc = useCTAVisualSource(ctaVisual);

  const [inputValue, setInputValue] = useState("");

  const heroGraphic = useMemo(() => {
    if (!ctaVisualSrc) {
      return null;
    }

    if (typeof ctaVisual === "string" && isDefaultCTAVisual(ctaVisual)) {
      return <DefaultCTAVisualPreview id={ctaVisual} src={ctaVisualSrc} />;
    }

    return <CustomCTAVisualPreview src={ctaVisualSrc} />;
  }, [ctaVisual, ctaVisualSrc]);

  const tabTitle = browserTabTitle.trim() || businessName.trim() || "Live Chat";

  const sampleAgentGreeting =
    ctaTitle.trim() ||
    t({
      id: "client-support-page.live-chat-preview-sample-agent-greeting",
      defaultMessage: "Hello! How can i help you?",
    });

  const previewDisabledChatInput = (
    <ChatInput
      value={inputValue}
      onChange={setInputValue}
      onSend={() => {}}
      isDisabled
      inputClassName="min-h-[100px]"
    />
  );

  return (
    <BrowserWindowShell faviconSrc={faviconSrc} tabTitle={tabTitle}>
      <LiveChatUI.Root className="h-full">
        <LiveChatUI.Sidebar>
          <div className="px-2 pb-1 pt-3">
            <LiveChatUI.Logo
              src={businessLogoSrc ?? ""}
              size="md"
              title={businessName}
            />
          </div>

          <LiveChatUI.SidebarDivider />

          <div className="flex flex-col gap-2">
            <LiveChatUI.SidebarButton
              type="button"
              icon={<MessageBubbleIcon />}
              title={t({
                id: "client-support-page.new-chat",
                defaultMessage: "New Chat",
              })}
              isActive={screenMode === "welcome"}
              onClick={() => {}}
            />
            {customButton && (
              <LiveChatUI.SidebarButton
                type="button"
                icon={<UrlIcon />}
                title={customButton.title}
                onClick={() => {}}
              />
            )}
          </div>

          <LiveChatUI.SidebarDivider />

          <LiveChatUI.ConversationList
            title={t({
              id: "client-support-page.recents",
              defaultMessage: "Recents",
            })}
          >
            <LiveChatUI.ConversationListItem
              title={t({
                id: "client-support-page.live-chat-preview-sidebar-recent-welcome",
                defaultMessage: "Welcome!",
              })}
              isActive={screenMode === "sampleConversation"}
              onClick={() => {}}
            />
          </LiveChatUI.ConversationList>
        </LiveChatUI.Sidebar>

        <LiveChatUI.Content>
          <LiveChatUI.MobileTopBar
            endSlot={
              <LiveChatUI.Logo
                src={businessLogoSrc ?? ""}
                size="sm"
                title={businessName}
              />
            }
          />
          <LiveChatUI.ContentScrollable>
            <LiveChatUI.ContentBody>
              {screenMode === "welcome" ? (
                <LiveChatUI.WelcomeContainer>
                  <LiveChatUI.WelcomeHero
                    graphic={heroGraphic}
                    title={ctaTitle}
                    subtitle={ctaSubtitle}
                  />
                  <div className="flex w-full flex-col gap-2.5">
                    {previewDisabledChatInput}
                    {suggestions.length > 0 && (
                      <div className="flex w-full flex-wrap justify-center gap-2.5">
                        {suggestions.map((suggestion, index) => (
                          <div
                            key={+index}
                            title={suggestion}
                            className={cx(
                              "font-secondary flex h-[34px] max-w-full cursor-default items-center justify-center overflow-hidden truncate rounded-3xl px-[18px] text-[13px] font-medium",
                              "border-grey-300 text-dark border border-solid bg-white opacity-90"
                            )}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </LiveChatUI.WelcomeContainer>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                  <LiveChatAppSampleConversation
                    agentName={agentName}
                    agentImage={agentImage ?? ""}
                    senderMessageBackgroundColor={senderMessageBackgroundColor}
                    agentGreeting={sampleAgentGreeting}
                  />
                  {previewDisabledChatInput}
                </div>
              )}
            </LiveChatUI.ContentBody>
          </LiveChatUI.ContentScrollable>
          <ChatFooter />
        </LiveChatUI.Content>
      </LiveChatUI.Root>
    </BrowserWindowShell>
  );
};
